import { User } from 'next-firebase-auth';
import { fetcherAndLog } from '@utils/fetcher';
import { userToken } from '@utils/firebase/initAuth';
import { getBaseUrl } from '@utils/general-utils';

export interface Settings {
  name?: string;
  webOnboardingCompleted: boolean;
  firstSeenInWeb: string | null;
  userId: number;
}

export type UserActivityStreakType =
  | 'none'
  | 'progress'
  | 'freeze'
  | 'repair'
  | 'weekend_freeze';

export interface UserActivity {
  activeStreak: { end: string; start: string; length: number };
  activity: UserActivityDay[];
  longestStreak: { end: string; length: number; start: string };
}
export interface UserActivityDay {
  date: string;
  goal: number;
  lessonCount: number;
  chapterGoal: number;
  chapterCount: number;
  sparksGoal: number;
  sparksCount: number;
  streakType: 'none' | 'progress' | 'freeze' | 'repair' | 'weekend_freeze';
}

export interface UserProfile {
  name: string;
  biography: string;
  avatarUrl: string;
  isPremium: boolean;
  sparks: number;
  level: number;
  activeStreakLength: number;
  longestStreakLength: number;
  leaderboardFirstPlaceCount: number;
  leaderboardSecondPlaceCount: number;
  leaderboardThirdPlaceCount: number;
  isFollowedByMe: boolean;
  leaderboardInfo: {
    currentLeague: number;
    trophies: UserTrophy[];
  };
  progress: UserProgress[];
}

export type UserTrophy = {
  league: number;
  firstPlaceCount: number;
  secondPlaceCount: number;
  thirdPlaceCount: number;
};

export type UserProgress = {
  trackId: number;
  trackTitle: string;
  isCompleted: boolean;
};

export async function fetchSettings(user: User): Promise<Settings> {
  const token = await userToken(user);
  const url = `${getBaseUrl(false)}/user/settings`;
  const result = await fetcherAndLog(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  });

  if (!result.ok) {
    const { message } = await result.json();
    throw new Error(
      `Could not get user settings - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}

export async function fetchUserActivity(
  user: User,
  from: string,
  to: string,
): Promise<UserActivity> {
  const token = await userToken(user);
  const url = `${getBaseUrl(false)}/user/activity?from=${from}&to=${to}`;
  const result = await fetcherAndLog(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  });

  if (!result.ok) {
    const { message } = await result.json();
    throw new Error(
      `Could not get user settings - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}

export async function postSavedFiles(
  trackId: string,
  courseId: string,
  chapterId: string,
  lessonId: number,
  publishSetVersion: number,
  files: any[],
  user: User,
) {
  const token = await userToken(user);
  const url = `${getBaseUrl(
    false,
  )}/tracks/${trackId}/tutorials/${courseId}/chapters/${chapterId}/lessons/${lessonId}/savefiles?publishSetVersion=${publishSetVersion}`;
  const body = {
    files: files.map((file) => ({
      userContent: file.content,
      name: file.name,
    })),
  };
  const result = await fetcherAndLog(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
  });

  if (!result.ok) {
    const { message } = await result.json();
    throw new Error(
      `Could not post saved files - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result;
}

export async function postSettings(
  user: User,
  webOnboardingCompleted: boolean,
  name?: string,
): Promise<Settings> {
  const token = await userToken(user);
  const url = `${getBaseUrl(false)}/user/settings`;
  const body = {
    webOnboardingCompleted,
    name,
  };
  const result = await fetcherAndLog(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
  });

  if (!result.ok) {
    const { message } = await result.json();
    throw new Error(
      `Could not patch user settings - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }
  return result.json();
}

export async function fetchUserProfile(user: User): Promise<UserProfile> {
  const { userId } = await fetchSettings(user);

  if (!userId) {
    throw new Error(`Could not get user ID`);
  }

  const token = await userToken(user);
  const url = `${getBaseUrl(false)}/users/${userId}/profile`;
  const result = await fetcherAndLog(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  });

  if (!result.ok || !result.body) {
    const { message } = await result.json();
    throw new Error(
      `Could not get user profile - ${result.statusText} (${result.status})${
        message ? `: ${message}` : ''
      }`,
    );
  }

  return result.json();
}
